import {
	createAsyncThunk,
	createSlice,
	nanoid,
	type PayloadAction,
} from '@reduxjs/toolkit'
import i18next from 'i18next'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type { ReduxContact as ReduxContacts } from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { fetchContactsByTenantIdUtil } from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'

type AsyncThunkConfig = {
	dispatch: ThunkAppDispatch
	state: RootState
	rejectValue: string
}

type ContactEditModalStatus = {
	isOpen: boolean
	section?: string
}

type ImportContactsPayload = {
	contactsData: Array<Partial<ReduxContacts>>
}

type ContactsState = {
	contactsData: ReduxContacts[]
	fetchContactsApiStatus: ApiStatus
	createOrUpdateContactApiStatus: ApiStatus
	viewContactStatus: ApiStatus
	deleteContactApiStatus: ApiStatus
	importClientsApiStatus: ApiStatus
	createContactModalOpen: boolean
	importClientsModalOpen: boolean
	importClientsMessageDialog: boolean
	viewContactOpen: boolean
	contactDeleteDialogOpenStatus: boolean
	currentContact: ReduxContacts | null
	contactEditModalStatus: ContactEditModalStatus
	editContactMenuAnchorEl: HTMLElement | null
	error: string | null
}

const initialState: ContactsState = {
	contactsData: [],
	fetchContactsApiStatus: 'idle',
	createOrUpdateContactApiStatus: 'idle',
	viewContactStatus: 'idle',
	deleteContactApiStatus: 'idle',
	importClientsApiStatus: 'idle',
	createContactModalOpen: false,
	importClientsModalOpen: false,
	importClientsMessageDialog: false,
	viewContactOpen: false,
	contactDeleteDialogOpenStatus: false,
	currentContact: null,
	contactEditModalStatus: { isOpen: false },
	editContactMenuAnchorEl: null,
	error: null,
}

// Fetches contacts that has users tenantId and the active flag is true
export const fetchContacts = createAsyncThunk<
	ReduxContacts[],
	void,
	AsyncThunkConfig
>('contacts/fetchContacts', async (_, { getState, rejectWithValue }) => {
	const { auth } = getState()
	try {
		const contactsData = await fetchContactsByTenantIdUtil(
			auth.userTableInfo?.currentCompanyTenantId
		)
		return contactsData as ReduxContacts[]
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const getContactDetails = createAsyncThunk<
	ReduxContacts,
	string,
	AsyncThunkConfig
>('contacts/getContactDetails', async (contactId, { rejectWithValue }) => {
	try {
		const {
			data: { getContact: items },
		} = (await API.graphql(
			graphqlOperation(queries.getContact, {
				id: contactId,
			})
		)) as {
			data: { getContact: ReduxContacts }
		}

		return items
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

// Creates a new contact item with info provided by user and then fetches contacts again
export const createContact = createAsyncThunk<
	void,
	Partial<ReduxContacts>,
	AsyncThunkConfig
>(
	'clients/createContact',
	async (contactInfo, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const newContact = {
				id: nanoid(),
				tenantId,
				activeFlag: true,
				...contactInfo,
			}

			await API.graphql(
				graphqlOperation(mutations.createContact, { input: newContact })
			)
			makeSuccessToast(i18next.t('contacts:toasts.addedContact'))
			dispatch(fetchContacts())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const importClients = createAsyncThunk<
	void,
	ImportContactsPayload,
	AsyncThunkConfig
>(
	'clients/importClients',
	async ({ contactsData }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			if (contactsData?.length > 0) {
				await Promise.all(
					contactsData.map(async (contact) => {
						const existingSubAccount =
							contact.clientSubAccount ?? ''
						const contactName = contact.name ?? ''
						let newSubAccount: string
						if (existingSubAccount !== '') {
							newSubAccount = `${existingSubAccount}`
						} else if (contactName.length > 6) {
							newSubAccount = contactName.substring(0, 6)
						} else {
							newSubAccount = contactName.padEnd(6, '0')
						}
						const newContact = {
							id: nanoid(),
							tenantId,
							activeFlag: true,
							...contact,
							clientSubAccount: newSubAccount,
							contactDetails: [
								{
									id: nanoid(),
									fullName: '',
									email: '',
									phone: '',
								},
							],
						}

						await API.graphql(
							graphqlOperation(mutations.createContact, {
								input: newContact,
							})
						)
					})
				)
				makeSuccessToast(i18next.t('contacts:toasts.addedContact'))
				dispatch(fetchContacts())
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Updates the contacts & fetches the contacts again
export const updateContact = createAsyncThunk<
	void,
	{ contactId: string; newContact: Partial<ReduxContacts> },
	AsyncThunkConfig
>(
	'clients/updateContact',
	async ({ contactId, newContact }, { dispatch, rejectWithValue }) => {
		try {
			const {
				data: { getContact },
			} = (await API.graphql(
				graphqlOperation(queries.getContact, { id: contactId })
			)) as {
				data: { getContact: ReduxContacts }
			}

			const updateContactInput = {
				id: getContact.id,
				...newContact,
			}

			await API.graphql(
				graphqlOperation(mutations.updateContact, {
					input: updateContactInput,
				})
			)
			makeSuccessToast(i18next.t('contacts:toasts.updatedContact'))
			dispatch(fetchContacts())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Deletes the contacts from id & fetches the contacts again
export const deleteContact = createAsyncThunk<
	void,
	{ contactInfo: ReduxContacts | null },
	AsyncThunkConfig
>(
	'clients/deleteContact',
	async ({ contactInfo }, { dispatch, rejectWithValue }) => {
		try {
			if (!contactInfo) {
				return rejectWithValue('Contact info is not set')
			}
			await API.graphql(
				graphqlOperation(mutations.updateContact, {
					input: {
						id: contactInfo.id,
						activeFlag: false,
					},
				})
			)
			makeSuccessToast(i18next.t('contacts:toasts.deletedContact'))
			dispatch(fetchContacts())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const contactsSlice = createSlice({
	name: 'contacts',
	initialState,
	reducers: {
		setCreateContactModalOpen: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.createContactModalOpen = action.payload.status
		},
		setImportClientsModalOpen: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.importClientsModalOpen = action.payload.status
		},
		setViewContactOpen: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.viewContactOpen = action.payload.status
			if (!action.payload.status) {
				state.currentContact = null
			}
		},
		setContactDeleteDialogOpenStatus: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.contactDeleteDialogOpenStatus = action.payload.status
			if (!action.payload.status) {
				state.currentContact = null
			}
		},
		setEditContactMenuAnchorEl: (
			state,
			action: PayloadAction<HTMLElement | null>
		) => {
			state.editContactMenuAnchorEl =
				action.payload as typeof state.editContactMenuAnchorEl
		},
		setContactEditModalStatus: (
			state,
			action: PayloadAction<ContactEditModalStatus>
		) => {
			state.contactEditModalStatus = action.payload
			if (action.payload.isOpen === false) {
				state.currentContact = null
			}
		},
		setImportClientsMessageDialog: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.importClientsMessageDialog = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchContacts.pending, (state, action) => {
			state.fetchContactsApiStatus = 'loading'
		})
		builder.addCase(fetchContacts.fulfilled, (state, action) => {
			state.fetchContactsApiStatus = 'succeeded'
			state.contactsData = action.payload
		})
		builder.addCase(fetchContacts.rejected, (state, action) => {
			state.fetchContactsApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(createContact.pending, (state, action) => {
			state.createOrUpdateContactApiStatus = 'loading'
		})
		builder.addCase(createContact.fulfilled, (state, action) => {
			state.createOrUpdateContactApiStatus = 'succeeded'
			state.createContactModalOpen = false
		})
		builder.addCase(createContact.rejected, (state, action) => {
			state.createOrUpdateContactApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(importClients.pending, (state, action) => {
			state.importClientsApiStatus = 'loading'
		})
		builder.addCase(importClients.fulfilled, (state, action) => {
			state.importClientsApiStatus = 'succeeded'
			state.importClientsModalOpen = false
			state.importClientsMessageDialog = true
		})
		builder.addCase(importClients.rejected, (state, action) => {
			state.importClientsApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(getContactDetails.pending, (state, action) => {
			state.viewContactStatus = 'loading'
		})
		builder.addCase(getContactDetails.fulfilled, (state, action) => {
			state.viewContactStatus = 'succeeded'
			state.currentContact = action.payload
		})
		builder.addCase(getContactDetails.rejected, (state, action) => {
			state.viewContactStatus = 'failed'
			state.currentContact = null
			state.error = action.error.message ?? null
		})
		builder.addCase(updateContact.pending, (state, action) => {
			state.createOrUpdateContactApiStatus = 'loading'
		})
		builder.addCase(updateContact.fulfilled, (state, action) => {
			state.createOrUpdateContactApiStatus = 'succeeded'
			state.contactEditModalStatus = { isOpen: false }
			state.currentContact = null
		})
		builder.addCase(updateContact.rejected, (state, action) => {
			state.createOrUpdateContactApiStatus = 'failed'
			state.contactEditModalStatus = { isOpen: false }
			state.error = action.error.message ?? null
		})
		builder.addCase(deleteContact.pending, (state, action) => {
			state.deleteContactApiStatus = 'loading'
		})
		builder.addCase(deleteContact.fulfilled, (state, action) => {
			state.deleteContactApiStatus = 'succeeded'
			state.contactDeleteDialogOpenStatus = false
		})
		builder.addCase(deleteContact.rejected, (state, action) => {
			state.deleteContactApiStatus = 'failed'
			state.contactDeleteDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
	},
})

export const {
	setCreateContactModalOpen,
	setImportClientsModalOpen,
	setImportClientsMessageDialog,
	setViewContactOpen,
	setContactDeleteDialogOpenStatus,
	setEditContactMenuAnchorEl,
	setContactEditModalStatus,
} = contactsSlice.actions

export default contactsSlice.reducer
